import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseHttpService } from '../../../shared/services/base-http.service';
import { MailboxProvider } from '../interfaces/mailbox-provider.interface';
import { MailboxConnection, CreateMailboxConnectionRequest, UpdateMailboxConnectionRequest } from '../interfaces/mailbox-connection.interface';

export interface ApiResponse<T> {
  isSuccess: boolean;
  code: number;
  message: string;
  data: T;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class MailboxService {
  private readonly endpoint = '/Mailbox';

  constructor(private http: BaseHttpService) {}

  getProviders(): Observable<ApiResponse<MailboxProvider[]>> {
    return this.http.get<ApiResponse<MailboxProvider[]>>(`${this.endpoint}/Providers`);
  }

  getConnections(): Observable<ApiResponse<MailboxConnection[]>> {
    return this.http.get<ApiResponse<MailboxConnection[]>>(`${this.endpoint}/Connections`);
  }

  createConnection(request: CreateMailboxConnectionRequest): Observable<ApiResponse<MailboxConnection>> {
    return this.http.post<ApiResponse<MailboxConnection>>(`${this.endpoint}/Connections`, request);
  }

  updateConnection(request: UpdateMailboxConnectionRequest): Observable<ApiResponse<MailboxConnection>> {
    return this.http.put<ApiResponse<MailboxConnection>>(`${this.endpoint}/Connections/${request.id}`, request);
  }

  deleteConnection(id: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.endpoint}/Connections/${id}`);
  }

  testConnection(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.endpoint}/Connections/${id}/Test`, {});
  }

  // OAuth authorize endpoints
  getZohoAuthorizeUrl(): Observable<{ authorizationUrl: string }> {
    return this.http.get<{ authorizationUrl: string }>(`${this.endpoint}/Zoho/authorize`);
  }

  getOutlookAuthorizeUrl(): Observable<{ authorizationUrl: string }> {
    return this.http.get<{ authorizationUrl: string }>(`${this.endpoint}/Outlook/authorize`);
  }

  getMessages(connectionId: number, folderId?: string, page: number = 1, limit: number = 20): Observable<ApiResponse<any[]>> {
    let url = `${this.endpoint}/messages?connectionId=${connectionId}&page=${page}&limit=${limit}`;
    if (folderId) {
      url += `&folderId=${folderId}`;
    }
    return this.http.get<ApiResponse<any[]>>(url);
  }

  syncMailbox(connectionId: number): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.endpoint}/sync?connectionId=${connectionId}`, {});
  }

  clearMailbox(connectionId: number): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.endpoint}/ClearAllEmails?connectionId=${connectionId}`, {});
  }

  // Backward-compatible Zoho-specific methods
  getZohoMessages(connectionId: number, folderId?: string, page: number = 1, limit: number = 20): Observable<ApiResponse<any[]>> {
    return this.getMessages(connectionId, folderId, page, limit);
  }

  syncZohoMailbox(connectionId: number): Observable<ApiResponse<any>> {
    return this.syncMailbox(connectionId);
  }
}
